import { useState, useEffect, useCallback } from 'react';
import { api, ApiError } from '../lib/api';
import { DataTable, type Column } from '../components/DataTable';
import type { StaffView } from '../lib/types';

/**
 * ⚠ Must MIRROR UserRole in the backend. The three narrow roles were added to the enum and to the
 * auth policies — but THIS DROPDOWN WAS MISSED. So they existed and could not be assigned to anyone,
 * which is why you couldn't see them.
 */
const ROLES = [
  'TrustAdmin', 'CampusAdmin', 'Principal', 'Accountant', 'Teacher',
  'Parent', 'Student',
  'Security', 'Warden', 'Librarian',
  'LabTechnician', 'StoreKeeper', 'SportsCoordinator', 'TransportManager', 'Receptionist', 'HrManager',
];

/** What each role actually gets. "Warden" on its own tells an admin nothing. */
const ROLE_HINT: Record<string, string> = {
  TrustAdmin: 'Everything, across the whole trust',
  CampusAdmin: 'Everything at their campus',
  Accountant: 'Fees and finance',
  Teacher: 'Their classes — attendance, marks, homework, messaging parents',
  Parent: 'Their own children only',
  Student: 'Their own record only',
  Security: 'The gate only. Scans passes. No roster, no fees, no results.',
  Warden: 'The hostel only. Not the fee ledger.',
  Librarian: 'The library only. Not the roster, not the fees.',
  Principal: 'Head of school. Sees everything and approves — but does not configure the system.',
  LabTechnician: 'Laboratory equipment and stock only. No roster, no marks.',
  StoreKeeper: 'The school store: catalogue, stock, allocations and parent orders.',
  SportsCoordinator: 'Sports equipment, events, results and the house leaderboard.',
  TransportManager: 'Routes, vehicles and trips. Not the fee ledger.',
  Receptionist: 'Front desk: admission enquiries and student requests. No fees, no marks.',
  HrManager: 'Staff records, recruitment, leave and attendance. NOT payroll — that moves money.',
};
const ROLE_LABEL: Record<string, string> = {
  TrustAdmin: 'Trust Admin', CampusAdmin: 'Campus Admin', Accountant: 'Accountant',
  Teacher: 'Teacher', Parent: 'Parent', Student: 'Student',
  // Without these, the new roles rendered as BLANK options — the dropdown looked broken.
  Security: 'Security (gate)', Warden: 'Warden (hostel)', Librarian: 'Librarian',
  Principal: 'Principal / Head', LabTechnician: 'Lab Technician', StoreKeeper: 'Store Keeper',
  SportsCoordinator: 'Sports Coordinator', TransportManager: 'Transport Manager',
  Receptionist: 'Receptionist (front desk)', HrManager: 'HR Manager',
};

export function AdminPage({ embedded = false }: { embedded?: boolean }) {
  const [rows, setRows] = useState<StaffView[]>([]);
  const [loading, setLoading] = useState(true);
  const [inviting, setInviting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { setRows(await api.get<StaffView[]>('/api/staff')); }
    catch (e) { setError(e instanceof ApiError && e.status === 403 ? 'Only school admins can manage staff.' : 'Could not load staff.'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setRole = async (u: StaffView, role: string) => {
    await api.put(`/api/staff/${u.id}/role`, { role });
    load();
  };
  const toggle = async (u: StaffView) => {
    await api.post(`/api/staff/${u.id}/${u.isActive ? 'deactivate' : 'reactivate'}`);
    load();
  };

  /**
   * Columns.
   *
   * ⚠ Inside the component: the Role cell is an interactive <select> wired to setRole, and the
   * action button calls toggle. Neither is reachable from module scope.
   */
  const columns: Column<typeof rows[number]>[] = [
    {
      id: 'name', header: 'Name', sortable: true, primary: true,
      // Email folded into the search value — an admin looking for someone usually has the address,
      // not the exact spelling of a display name.
      value: (u) => `${u.displayName ?? ''} ${u.email ?? ''}`,
      cell: (u) => <strong>{u.displayName || '—'}</strong>,
    },
    {
      id: 'email', header: 'Email', sortable: true, hideSm: true,
      value: (u) => u.email ?? '',
      cell: (u) => <span className="mono" style={{ fontSize: 12 }}>{u.email ?? '—'}</span>,
    },
    {
      id: 'role', header: 'Role', sortable: true,
      // Sorts on the LABEL, not the enum name — "Security (gate)" should sit where a reader expects
      // it alphabetically, not under whatever the code calls it.
      value: (u) => ROLE_LABEL[u.role] ?? u.role,
      cell: (u) => (
        <select
          className="input"
          value={u.role}
          onChange={(e) => setRole(u, e.target.value)}
          onClick={(e) => e.stopPropagation()}
          style={{ padding: '4px 8px', fontSize: 13, minWidth: 130 }}
        >
          {ROLES.map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
        </select>
      ),
    },
    {
      id: 'lastLogin', header: 'Last login', sortable: true, hideMd: true,
      // ⚠ Never-logged-in sorts FIRST with an empty string. An admin auditing accounts wants the
      // ones nobody has ever used — those are the ones to disable.
      value: (u) => u.lastLoginAt ?? '',
      cell: (u) => <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>{u.lastLoginAt ?? '—'}</span>,
    },
    {
      id: 'status', header: 'Status', sortable: true,
      value: (u) => (u.isActive ? 'Active' : 'Disabled'),
      cell: (u) => (
        <span className={`pill ${u.isActive ? 'pill-ok' : 'pill-danger'}`} style={{ fontSize: 11 }}>
          {u.isActive ? 'Active' : 'Disabled'}
        </span>
      ),
    },
    {
      id: 'action', header: '', align: 'right', actions: true,
      cell: (u) => (
        <button
          className="btn"
          style={{ padding: '4px 12px', fontSize: 13 }}
          onClick={(e) => { e.stopPropagation(); toggle(u); }}
        >
          {u.isActive ? 'Disable' : 'Enable'}
        </button>
      ),
    },
  ];

  return (
    <div>
      {!embedded && (
        <div style={{ marginBottom: 20 }}>
          <div className="eyebrow" style={{ marginBottom: 6 }}>Admin</div>
          <h1 className="h-display" style={{ fontSize: 28 }}>Staff &amp; access</h1>
          <p style={{ color: 'var(--ink-2)', marginTop: 4, maxWidth: 640 }}>
            Manage who can sign in to your school and what they can do. Invited staff sign in with
            their email — a one-time code is sent, no password needed.
          </p>
        </div>
      )}

      {error ? (
        <div className="card" style={{ padding: 28, textAlign: 'center', color: 'var(--ink-2)' }}>{error}</div>
      ) : (
        <>
          <div style={{ display: 'flex', marginBottom: 14 }}>
            <div style={{ color: 'var(--ink-3)', fontSize: 13, alignSelf: 'center' }}>{rows.length} {rows.length === 1 ? 'person' : 'people'}</div>
            <button className="btn btn-accent" style={{ marginLeft: 'auto' }} onClick={() => setInviting(true)}>+ Invite staff</button>
          </div>

          <DataTable
            rows={loading ? null : rows}
            columns={columns}
            rowKey={(u) => u.id}

            search
            searchPlaceholder="Search by name, email or role…"

            // Client-side: a school's staff list is tens of people, and the whole set is already
            // loaded to render the role dropdowns.
            pageSize={25}

            empty={{ title: 'No staff yet', body: 'Invite the first person to get started.' }}
          />

        </>
      )}

      {inviting && <InviteDialog onClose={() => setInviting(false)} onDone={() => { setInviting(false); load(); }} />}
    </div>
  );
}

function InviteDialog({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [email, setEmail] = useState(''); const [name, setName] = useState('');
  const [role, setRole] = useState('Teacher');
  const [busy, setBusy] = useState(false); const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true); setError(null);
    try {
      await api.post('/api/staff', { email: email.trim().toLowerCase(), displayName: name.trim(), role });
      onDone();
    } catch (e) { setError(e instanceof ApiError ? e.message : 'Could not invite.'); }
    finally { setBusy(false); }
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(10,12,18,.45)', zIndex: 50, display: 'grid', placeItems: 'center', padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()} className="card" style={{ width: 440, maxWidth: '100%', padding: 24 }}>
        <h2 className="h-display" style={{ fontSize: 20, marginBottom: 16 }}>Invite staff</h2>
        {error && <div className="pill pill-danger" style={{ padding: '8px 12px', borderRadius: 'var(--r-md)', marginBottom: 12, justifyContent: 'flex-start' }}>{error}</div>}
        <label className="label">Email</label>
        <input className="input" type="email" autoFocus value={email} onChange={(e) => setEmail(e.target.value)} placeholder="teacher@school.edu" style={{ marginBottom: 12 }} />
        <label className="label">Name</label>
        <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" style={{ marginBottom: 12 }} />
        <label className="label">Role</label>
        <select className="input" value={role} onChange={(e) => setRole(e.target.value)}>
          {ROLES.map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
        </select>
        {/* An admin choosing "Warden" deserves to know what that actually means. */}
        <p style={{ fontSize: 12.5, color: 'var(--ink-3)', margin: '6px 0 20px' }}>
          {ROLE_HINT[role]}
        </p>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn" style={{ flex: 1, justifyContent: 'center' }} onClick={onClose}>Cancel</button>
          <button className="btn btn-accent" style={{ flex: 1, justifyContent: 'center' }} disabled={!email.trim() || !name.trim() || busy} onClick={submit}>
            {busy ? 'Inviting…' : 'Send invite'}
          </button>
        </div>
      </div>
    </div>
  );
}

